import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';

export interface QuickMessageTarget {
  recipientId: number;
  recipientName: string;
  celebrationId?: number;
}

interface ConversationContextType {
  isConversationVisible: boolean;
  showConversationBubble: boolean;
  pendingCommand: string | null;
  openConversation: () => void;
  closeConversation: () => void;
  toggleConversation: () => void;
  openQuickMessage: (target: QuickMessageTarget) => void;
  consumePendingCommand: () => string | null;
}

const ConversationContext = createContext<ConversationContextType | undefined>(undefined);

const STORAGE_KEY = 'om-conversation-visibility';

interface ConversationProviderProps {
  children: ReactNode;
}

export const ConversationProvider: React.FC<ConversationProviderProps> = ({ children }) => {
  // Load initial state from localStorage, default to visible
  const [isConversationVisible, setIsConversationVisible] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : true; // Default to visible
    } catch {
      return true; // Default to visible if localStorage is not available
    }
  });

  // Show bubble when conversation is closed
  const showConversationBubble = !isConversationVisible;

  // Persist state to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(isConversationVisible));
    } catch {
      // Ignore localStorage errors
    }
  }, [isConversationVisible]);

  const openConversation = () => {
    setIsConversationVisible(true);
  };

  const closeConversation = () => {
    setIsConversationVisible(false);
  };

  const toggleConversation = () => {
    setIsConversationVisible(!isConversationVisible);
  };

  const [pendingCommand, setPendingCommand] = useState<string | null>(null);

  const openQuickMessage = useCallback((target: QuickMessageTarget) => {
    setPendingCommand(`/message @${target.recipientName} `);
    setIsConversationVisible(true);
  }, []);

  const consumePendingCommand = useCallback(() => {
    const cmd = pendingCommand;
    setPendingCommand(null);
    return cmd;
  }, [pendingCommand]);

  const value: ConversationContextType = {
    isConversationVisible,
    showConversationBubble,
    pendingCommand,
    openConversation,
    closeConversation,
    toggleConversation,
    openQuickMessage,
    consumePendingCommand,
  };

  return (
    <ConversationContext.Provider value={value}>
      {children}
    </ConversationContext.Provider>
  );
};

export const useConversation = (): ConversationContextType => {
  const context = useContext(ConversationContext);
  if (context === undefined) {
    throw new Error('useConversation must be used within a ConversationProvider');
  }
  return context;
};
